import { startDiscussionPcm, type DiscussionCapture } from './discussionCapture';
export type { DiscussionCapture } from './discussionCapture';
type RecognitionResult = { readonly isFinal: boolean; readonly 0: { readonly transcript: string } };
type RecognitionEvent = { readonly resultIndex: number; readonly results: ArrayLike<RecognitionResult> };
type RecognitionError = { readonly error?: string };
type NativeRecognition = {
  lang: string; continuous: boolean; interimResults: boolean; maxAlternatives: number;
  onresult: ((event: RecognitionEvent) => void) | null;
  onerror: ((event: RecognitionError) => void) | null;
  onend: (() => void) | null;
  onstart?: (() => void) | null;
  onaudiostart?: (() => void) | null;
  start(audioTrack?: MediaStreamTrack): void; stop(): void; abort(): void;
};
type RecognitionConstructor = new () => NativeRecognition;
type VoiceWindow = Window & { SpeechRecognition?: RecognitionConstructor; webkitSpeechRecognition?: RecognitionConstructor };
export type CantoneseSpeechItem = {
  id: string; text: string; speakerId?: string; voiceGender?: 'male' | 'female';
  // Server voice rate/pitch are already synthesized into cloud audio. Keep
  // provider delivery metadata out of these client playback requests.
  speechRequest?: { sessionId: string; turnId: string } | { avatarId: string };
};

// Recognition and owned playback must not listen over or interrupt each other.
let activeOperation: symbol | null = null;
const abortError = () => new DOMException('Voice operation cancelled.', 'AbortError');
const busyError = () => new Error('Another voice operation is active. Stop it before starting again.');

function recognitionConstructor() {
  if (typeof window === 'undefined') return undefined;
  const target = window as VoiceWindow;
  return target.SpeechRecognition ?? target.webkitSpeechRecognition;
}

export function supportsBrowserRecognition(): boolean { return Boolean(recognitionConstructor()); }

function usesOwnedChromeMicrophone(): boolean {
  if (typeof navigator === 'undefined') return false;
  const agent = navigator.userAgent;
  if (/Android|Mobile|CriOS|\b(?:Edg|Edge|EdgA|EdgiOS)\//i.test(agent)) return false;
  const version = /\b(?:Chrome|HeadlessChrome)\/(\d+)/.exec(agent)?.[1];
  return Boolean(version && Number(version) >= 135);
}

function stopStreamTracks(stream: MediaStream) {
  for (const track of stream.getTracks()) {
    try { track.stop(); } catch { /* still release the remaining owned tracks */ }
  }
}

function recognitionError(error: unknown): Error {
  const code = typeof error === 'object' && error !== null && 'error' in error
    ? String(error.error)
    : typeof error === 'object' && error !== null && 'name' in error ? String(error.name) : '';
  if (['not-allowed', 'service-not-allowed', 'NotAllowedError', 'SecurityError'].includes(code)) {
    return new Error('Microphone or speech recognition permission is blocked. Allow access in the browser and try again, or type your response.');
  }
  if (['audio-capture', 'NotFoundError', 'DevicesNotFoundError'].includes(code)) return new Error('No working microphone was found. Check your device or type your response.');
  if (code === 'NotReadableError' || code === 'TrackStartError') return new Error('The microphone is busy or unavailable. Close other recording apps and try again, or type your response.');
  if (code === 'no-speech') return new Error('No speech was heard. Try again or type your response.');
  if (code === 'network') return new Error('The browser speech service could not connect. Try again or type your response.');
  if (code === 'aborted' || code === 'AbortError') return abortError();
  return new Error('Cantonese speech recognition could not finish. Try again or type your response.');
}

export function recognizeCantonese(options: { signal: AbortSignal; onInterim?: (text: string) => void; onListening?: () => void; onRecording?: (capture: DiscussionCapture) => Promise<void>; recordingSource?: DiscussionCapture['source'] }): Promise<string> {
  if (options.signal.aborted) return Promise.reject(abortError());
  const Recognition = recognitionConstructor();
  if (!Recognition) return Promise.reject(new Error('This browser does not support speech recognition. Use a supported browser or type your response.'));
  if (activeOperation) return Promise.reject(busyError());
  const owner = Symbol('recognition');
  activeOperation = owner;
  return new Promise<string>((resolve, reject) => {
    let recognizer: NativeRecognition | undefined;
    let ownedStream: MediaStream | null = null;
    let settled = false;
    let listening = false;
    let timer: number | undefined;
    let pcm: ReturnType<typeof startDiscussionPcm> | null = null, latestTranscript = '', nativeStarted = false, nativeEnded = false;
    let startedAt = '', captureId = '';
    const finals = new Map<number, string>();
    const releaseMicrophone = () => {
      const stream = ownedStream;
      ownedStream = null;
      if (stream) stopStreamTracks(stream);
    };
    const finalText = () => [...finals.entries()].sort(([a], [b]) => a - b).map(([, text]) => text).join('').replace(/\s+/g, ' ').trim();
    const finish = (error?: Error, text?: string) => {
      if (settled) return;
      settled = true;
      if (timer !== undefined) window.clearTimeout(timer);
      options.signal.removeEventListener('abort', cancel);
      let nativeStopped: Promise<boolean> = Promise.resolve(true);
      if (recognizer) {
        recognizer.onresult = null; recognizer.onerror = null; recognizer.onend = null;
        recognizer.onstart = null; recognizer.onaudiostart = null;
        if (options.onRecording && nativeStarted && !nativeEnded) nativeStopped = new Promise<boolean>(resolve => {
          const timeout = window.setTimeout(() => { if (recognizer) recognizer.onend = null; resolve(false); }, 1000);
          recognizer!.onend = () => { nativeEnded = true; window.clearTimeout(timeout); recognizer!.onend = null; resolve(true); };
        });
        try { error ? recognizer.abort() : recognizer.stop(); } catch { /* already stopped */ }
      }
      const captured = pcm?.stop(); pcm = null;
      releaseMicrophone();
      if (!options.onRecording) { if (activeOperation === owner) activeOperation = null; error ? reject(error) : resolve(text ?? ''); return; }
      void (async () => {
        try {
          const microphoneStopped = await nativeStopped;
          if (captured) await options.onRecording!({ id: captureId, blob: await captured, startedAt, source: options.recordingSource ?? 'voice', transcript: (text || latestTranscript || finalText()).trim().slice(0, 1200) });
          if (!microphoneStopped) throw new Error('未能確認語音辨認已停止；錄音已保存，請先停止瀏覽器收音再重試。Recording saved, but microphone shutdown was not confirmed. Stop browser capture before retrying.');
          error ? reject(error) : resolve(text ?? '');
        } catch (problem) { reject(problem); }
        finally { if (activeOperation === owner) activeOperation = null; }
      })();
    };
    const cancel = () => finish(abortError());
    options.signal.addEventListener('abort', cancel, { once: true });
    const startWithOwnedTrack = async (native: NativeRecognition, useTrack: boolean) => {
      if (!navigator.mediaDevices?.getUserMedia) {
        finish(new Error('Microphone access is unavailable. Use HTTPS or localhost and a supported browser, or type your response.'));
        return;
      }
      let stream: MediaStream;
      try {
        stream = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true } });
      } catch (error) { finish(recognitionError(error)); return; }
      if (settled || options.signal.aborted) { stopStreamTracks(stream); return; }
      ownedStream = stream;
      const track = stream.getAudioTracks()[0];
      if (!track || track.kind !== 'audio' || track.readyState !== 'live') {
        finish(new Error('No live microphone audio track was available. Check your device or type your response.'));
        return;
      }
      if (options.onRecording) {
        try {
          captureId = crypto.randomUUID(); startedAt = new Date().toISOString(); pcm = startDiscussionPcm(stream);
          await pcm.ready;
          if (settled || options.signal.aborted) return;
        } catch { finish(new Error('未能啟動討論錄音，未開始語音辨認。Discussion recording could not start; use text or retry.')); return; }
      }
      try {
        // The track must deliver audio immediately; waiting for onstart here can deadlock.
        nativeStarted = true; useTrack ? native.start(track) : native.start();
      } catch (error) {
        if (!options.onRecording) releaseMicrophone();
        if (!(error instanceof TypeError) || !useTrack) { finish(recognitionError(error)); return; }
        // A rejected overload may use the legacy capture path, after our stream is stopped.
        try { native.start(); } catch (fallbackError) { finish(recognitionError(fallbackError)); }
      }
    };
    try {
      recognizer = new Recognition();
      recognizer.lang = 'zh-HK';
      recognizer.continuous = false;
      recognizer.interimResults = true;
      recognizer.maxAlternatives = 1;
      recognizer.onaudiostart = () => {
        if (!settled && !listening && !options.signal.aborted && activeOperation === owner) {
          listening = true;
          options.onListening?.();
        }
      };
      recognizer.onresult = event => {
        if (settled || options.signal.aborted) return;
        for (let index = Math.max(0, event.resultIndex); index < event.results.length; index += 1) {
          const result = event.results[index];
          if (result?.isFinal) finals.set(index, result[0]?.transcript ?? '');
        }
        const partial: string[] = [];
        for (let index = 0; index < event.results.length; index += 1) {
          const result = event.results[index];
          partial.push(result?.isFinal ? finals.get(index) ?? result[0]?.transcript ?? '' : result?.[0]?.transcript ?? '');
        }
        latestTranscript = partial.join('').trim(); options.onInterim?.(latestTranscript);
      };
      recognizer.onerror = event => { if (!settled) finish(recognitionError(event)); };
      recognizer.onend = () => {
        nativeEnded = true;
        if (settled) return;
        const text = finalText();
        text ? finish(undefined, text) : finish(new Error('No final Cantonese speech was recognised. Try again or type your response.'));
      };
      timer = window.setTimeout(() => finish(new Error('Speech recognition timed out. Try a shorter response or type instead.')), 25_000);
      if (usesOwnedChromeMicrophone() || options.onRecording) void startWithOwnedTrack(recognizer, usesOwnedChromeMicrophone());
      else recognizer.start();
      if (options.signal.aborted) cancel();
    } catch (error) { finish(recognitionError(error)); }
  });
}

function synthesisAvailable(): boolean {
  return typeof window !== 'undefined' && Boolean(window.speechSynthesis) && typeof window.SpeechSynthesisUtterance === 'function';
}

export function getCantoneseVoices(): SpeechSynthesisVoice[] {
  if (!synthesisAvailable()) return [];
  try {
    return window.speechSynthesis.getVoices().filter(voice => /^(?:zh[-_](?:hk|mo)(?:[-_]|$)|yue(?:[-_]|$))/i.test(voice.lang)
      || /cantonese|廣東話|粵語|粤语/i.test(`${voice.name} ${voice.voiceURI}`));
  } catch { return []; }
}

export function supportsCantonesePlayback(): boolean { return getCantoneseVoices().length > 0; }

export function supportsCloudPlayback(): boolean {
  return typeof window !== 'undefined' && typeof window.Audio === 'function'
    && typeof fetch === 'function' && typeof URL.createObjectURL === 'function' && typeof URL.revokeObjectURL === 'function';
}

let cloudAudio: HTMLAudioElement | null = null;
function ownedCloudAudio(): HTMLAudioElement {
  if (!cloudAudio) {
    cloudAudio = new window.Audio();
    cloudAudio.preload = 'auto';
    cloudAudio.setAttribute('playsinline', '');
  }
  return cloudAudio;
}

function clearCloudAudio(audio: HTMLAudioElement) {
  audio.onplaying = null; audio.onended = null; audio.onerror = null;
  try { audio.pause(); } catch { /* already stopped */ }
  audio.removeAttribute('src');
  try { audio.load(); } catch { /* source is already released */ }
}

function silentWav(): string {
  // 20 ms of unsigned 8-bit silence, used only to activate our own media element.
  const bytes = new Uint8Array(204);
  const view = new DataView(bytes.buffer);
  const label = (offset: number, text: string) => bytes.set([...text].map(character => character.charCodeAt(0)), offset);
  label(0, 'RIFF'); view.setUint32(4, 196, true); label(8, 'WAVE'); label(12, 'fmt ');
  view.setUint32(16, 16, true); view.setUint16(20, 1, true); view.setUint16(22, 1, true);
  view.setUint32(24, 8_000, true); view.setUint32(28, 8_000, true); view.setUint16(32, 1, true); view.setUint16(34, 8, true);
  label(36, 'data'); view.setUint32(40, 160, true); bytes.fill(128, 44);
  return `data:audio/wav;base64,${btoa(String.fromCharCode(...bytes))}`;
}

/** Call directly from a click handler, before fetching audio. Autoplay policies may still reject later playback. */
export function prepareCantonesePlayback(options: { signal?: AbortSignal } = {}): Promise<void> {
  if (options.signal?.aborted) return Promise.reject(abortError());
  if (!supportsCloudPlayback()) return Promise.reject(new Error('This browser does not support cloud audio playback. Continue with text.'));
  if (activeOperation) return Promise.reject(busyError());
  if (synthesisAvailable() && (window.speechSynthesis.speaking || window.speechSynthesis.pending)) return Promise.reject(new Error('Another speech playback is running. Stop it before starting voice practice.'));
  const owner = Symbol('prepare playback');
  activeOperation = owner;
  return new Promise<void>((resolve, reject) => {
    let settled = false;
    let audio: HTMLAudioElement | undefined;
    const finish = (error?: Error) => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timer);
      options.signal?.removeEventListener('abort', cancel);
      if (audio) {
        if (error) clearCloudAudio(audio);
        else { try { audio.pause(); } catch { /* silent primer already finished */ } }
      }
      if (activeOperation === owner) activeOperation = null;
      error ? reject(error) : resolve();
    };
    const cancel = () => finish(abortError());
    const timer = window.setTimeout(() => finish(new Error('Audio playback could not be prepared. Click Start again or continue with text.')), 1_500);
    options.signal?.addEventListener('abort', cancel, { once: true });
    try {
      audio = ownedCloudAudio();
      audio.src = silentWav();
      Promise.resolve(audio.play()).then(() => finish(), error => finish(playbackException(error)));
    } catch (error) { finish(playbackException(error)); }
    if (options.signal?.aborted) cancel();
  });
}

function waitForVoices(signal: AbortSignal): Promise<SpeechSynthesisVoice[]> {
  const initial = getCantoneseVoices();
  if (initial.length) return Promise.resolve(initial);
  return new Promise((resolve, reject) => {
    const synthesis = window.speechSynthesis;
    let settled = false;
    let timer: number;
    const finish = (error?: Error, voices?: SpeechSynthesisVoice[]) => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timer);
      synthesis.removeEventListener?.('voiceschanged', changed);
      signal.removeEventListener('abort', cancel);
      error ? reject(error) : resolve(voices ?? []);
    };
    const changed = () => { const voices = getCantoneseVoices(); if (voices.length) finish(undefined, voices); };
    const cancel = () => finish(abortError());
    synthesis.addEventListener?.('voiceschanged', changed);
    signal.addEventListener('abort', cancel, { once: true });
    timer = window.setTimeout(() => {
      const voices = getCantoneseVoices();
      voices.length ? finish(undefined, voices) : finish(new Error('No Cantonese playback voice is installed. Install a Cantonese voice for this device or continue with text.'));
    }, 1_500);
    if (signal.aborted) cancel();
  });
}

function selectVoice(item: CantoneseSpeechItem, voices: SpeechSynthesisVoice[]): SpeechSynthesisVoice {
  const speaker = item.speakerId ?? item.id;
  const named = item.voiceGender === 'female' ? /tracy/i : item.voiceGender === 'male' ? /danny/i
    : /chloe|嘉欣/i.test(speaker) ? /tracy/i : /alan|jason|阿朗|子軒/i.test(speaker) ? /danny/i : null;
  const preferred = named && voices.find(voice => named.test(`${voice.name} ${voice.voiceURI}`));
  if (preferred) return preferred;
  const index = [...speaker].reduce((sum, character) => sum + character.charCodeAt(0), 0) % voices.length;
  return voices[index];
}

function playbackError(code?: string): Error {
  if (code === 'not-allowed') return new Error('Speech playback was blocked by the browser. Start playback with a click, or continue with text.');
  if (code === 'canceled' || code === 'interrupted') return new DOMException('Speech playback was cancelled or interrupted.', 'AbortError');
  return new Error('Cantonese playback could not finish. Check device audio or continue with text.');
}

function playbackException(error: unknown): Error {
  const name = typeof error === 'object' && error !== null && 'name' in error ? String(error.name) : '';
  return playbackError(name === 'NotAllowedError' ? 'not-allowed' : undefined);
}

class CloudPlaybackError extends Error {}

export async function speakCantoneseQueue(items: CantoneseSpeechItem[], options: { signal: AbortSignal; onSpeaker?: (id: string | null) => void; rate?: number }): Promise<void> {
  if (options.signal.aborted) throw abortError();
  const spokenItems = items.filter(item => item.text.trim());
  if (!spokenItems.length) { options.onSpeaker?.(null); return; }
  if (spokenItems.some(item => !item.speechRequest) && !synthesisAvailable()) throw new Error('This browser does not support speech playback. Continue with text.');
  if (spokenItems.some(item => item.speechRequest) && !supportsCloudPlayback()) throw new Error('This browser does not support cloud audio playback. Continue with text.');
  if (activeOperation) throw busyError();
  const owner = Symbol('playback');
  activeOperation = owner;
  const synthesis = synthesisAvailable() ? window.speechSynthesis : null;
  // This is an optional listener playback speed, not a provider voice setting.
  const rate = Number.isFinite(options.rate) ? Math.min(2, Math.max(0.5, options.rate!)) : 1;
  let utterance: SpeechSynthesisUtterance | null = null;
  let cancelPending: ((error: Error) => void) | null = null;
  const notify = (id: string | null) => { try { options.onSpeaker?.(id); } catch { /* UI callbacks must not leave audio running */ } };
  const release = () => { if (activeOperation === owner) { activeOperation = null; notify(null); } };
  const cancel = () => {
    if (activeOperation !== owner) return;
    const playing = Boolean(utterance);
    cancelPending?.(abortError());
    if (playing) synthesis?.cancel();
    release();
  };
  options.signal.addEventListener('abort', cancel, { once: true });
  try {
    const voices = spokenItems.some(item => !item.speechRequest) ? await waitForVoices(options.signal) : [];
    if (options.signal.aborted) throw abortError();
    if (synthesis && (synthesis.speaking || synthesis.pending)) throw new Error('Another speech playback is running. Stop it before starting voice practice.');
    for (const item of spokenItems) {
      if (options.signal.aborted) throw abortError();
      if (item.speechRequest) {
        await new Promise<void>((resolve, reject) => {
          const request = item.speechRequest!;
          const endpoint = 'avatarId' in request
            ? `/api/canntalk/avatars/${encodeURIComponent(request.avatarId)}/voice-preview`
            : `/api/canntalk/sessions/${encodeURIComponent(request.sessionId)}/turns/${encodeURIComponent(request.turnId)}/speech`;
          const fetchController = new AbortController();
          let settled = false;
          let audio: HTMLAudioElement | null = null;
          let objectUrl: string | null = null;
          let timer: number;
          const finish = (error?: Error) => {
            if (settled) return;
            settled = true;
            window.clearTimeout(timer);
            fetchController.abort();
            if (audio) clearCloudAudio(audio);
            if (objectUrl) URL.revokeObjectURL(objectUrl);
            cancelPending = null;
            notify(null);
            error ? reject(error) : resolve();
          };
          cancelPending = error => finish(error);
          timer = window.setTimeout(() => finish(new Error('Cloud Cantonese audio request timed out. Try again or continue with text.')), 45_000);
          void (async () => {
            try {
              const response = await fetch(endpoint, { method: 'POST', credentials: 'same-origin', redirect: 'error', signal: fetchController.signal });
              if (settled || options.signal.aborted || activeOperation !== owner) return;
              if (!response.ok) {
                let voiceChanged = false;
                if (response.status === 409) {
                  try { const body = await response.json(); voiceChanged = body?.code === 'SPEECH_VOICE_CHANGED'; } catch { /* Keep malformed/conflicting responses generic. */ }
                  if (settled || options.signal.aborted || activeOperation !== owner) return;
                }
                throw new CloudPlaybackError(voiceChanged
                  ? '呢場練習使用舊聲線，請開一場新練習。Start a new practice to use the updated voices.'
                  : response.status === 422
                  ? '呢句含有非粵語措辭，已停止讀聲。Non-Cantonese wording detected; playback stopped.'
                  : response.status === 401 || response.status === 403
                    ? 'Cloud voice access was denied. Sign in again or continue with text.'
                    : 'Cloud Cantonese voice generation failed. Try again or continue with text.');
              }
              const type = response.headers.get('content-type')?.split(';')[0].trim().toLowerCase() ?? '';
              if (!['audio/wav', 'audio/x-wav', 'audio/wave', 'audio/mpeg', 'audio/mp3'].includes(type)) throw new CloudPlaybackError('Cloud voice returned an unsupported audio format. Continue with text.');
              const blob = await response.blob();
              if (settled || options.signal.aborted || activeOperation !== owner) return;
              if (!blob.size || blob.size > 20 * 1024 * 1024) throw new CloudPlaybackError('Cloud voice returned empty or oversized audio. Try again or continue with text.');
              if (synthesis && (synthesis.speaking || synthesis.pending)) throw new CloudPlaybackError('Another speech playback is running. Stop it before starting voice practice.');
              objectUrl = URL.createObjectURL(blob);
              audio = ownedCloudAudio();
              audio.playbackRate = rate;
              audio.onplaying = () => { if (!settled && !options.signal.aborted && activeOperation === owner) notify(item.speakerId ?? item.id); };
              audio.onended = () => finish();
              audio.onerror = () => finish(new Error('Cloud Cantonese audio could not be played. Check device audio or continue with text.'));
              window.clearTimeout(timer);
              timer = window.setTimeout(() => finish(new Error('Cloud Cantonese playback timed out. Try a shorter response or continue with text.')), Math.max(10_000, Math.min(120_000, 4_000 + item.text.length * 500 / rate)));
              audio.src = objectUrl;
              try { await audio.play(); } catch (error) { finish(playbackException(error)); }
            } catch (error) {
              if (settled) return;
              if (options.signal.aborted || activeOperation !== owner) finish(abortError());
              else finish(error instanceof CloudPlaybackError ? error : new Error('Cloud Cantonese audio could not connect. Try again or continue with text.'));
            }
          })();
          if (options.signal.aborted) cancelPending?.(abortError());
        });
        continue;
      }
      if (!synthesis) throw new Error('This browser does not support speech playback. Continue with text.');
      await new Promise<void>((resolve, reject) => {
        const current = new window.SpeechSynthesisUtterance(item.text.trim());
        utterance = current;
        let settled = false;
        let timer: number;
        const finish = (error?: Error, stopOwnedPlayback = false) => {
          if (settled) return;
          settled = true;
          window.clearTimeout(timer);
          current.onstart = null; current.onend = null; current.onerror = null;
          if (utterance === current) utterance = null;
          cancelPending = null;
          if (stopOwnedPlayback && activeOperation === owner) synthesis.cancel();
          // Match cloud playback: the current source has ended before the UI
          // decides whether to yield instead of starting the next queue item.
          notify(null);
          error ? reject(error) : resolve();
        };
        cancelPending = error => finish(error);
        current.voice = selectVoice(item, voices);
        current.lang = current.voice.lang;
        current.rate = rate;
        current.onstart = () => { if (!settled && !options.signal.aborted && activeOperation === owner) notify(item.speakerId ?? item.id); };
        current.onend = () => finish();
        current.onerror = event => finish(playbackError(event.error));
        timer = window.setTimeout(() => finish(new Error('Cantonese playback timed out. Try replaying a shorter response or continue with text.'), true), Math.max(10_000, Math.min(120_000, 4_000 + current.text.length * 500 / rate)));
        try { synthesis.speak(current); } catch (error) {
          finish(playbackException(error));
          return;
        }
        // Chrome ignores resume() on an empty renderer queue, and its paused getter
        // can lag the browser process. Resume only after our utterance is queued.
        if (!settled && activeOperation === owner && !options.signal.aborted) {
          try { synthesis.resume(); }
          catch (error) {
            finish(playbackException(error), true);
          }
        }
      });
    }
  } finally {
    options.signal.removeEventListener('abort', cancel);
    release();
  }
}
